import React, { useState, useMemo } from 'react';
import {
  Search,
  ChevronRight,
  ArrowRight,
  GraduationCap,
  ClipboardCheck,
  Lightbulb,
  TrendingUp,
  CalendarCheck,
  Check,
  ShieldCheck,
  Sparkles,
  Users,
  Star,
  Layers,
  Timer,
  Gift
} from 'lucide-react';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { UserProfile, AuthIntent, PraxisPaper } from '../types';
import { LiveActivityFeed } from './LiveActivityFeed';
import { ExamFamilyCard } from './ExamFamilyCard';
import { papersForFamily } from './brand/FamilyArt';

// =====================================================================
// Front page (2026-10-07 navy redesign, approved by Peter from mockup).
// Navy hero under the teal nav bar; pink kept only for "Get Started"
// buttons; teal for the main actions and links. Every claim on the page is
// true today: no invented learner counts or pass rates. The exam-family
// grid uses live paper counts (families without content show "Coming
// soon"), and Multi-State / National is always first.
// =====================================================================

interface LandingPageProps {
  onOpenAuthModal: (mode?: 'signin' | 'signup', intent?: AuthIntent) => void;
  setActiveTab: (tab: string) => void;
  onOpenPricingModal: () => void;
  onSelectExam?: (entry: ExamCatalogEntry) => void;
  userProfile?: UserProfile;
  // Live catalog (paper docs only) for the per-family paper counts.
  papers?: PraxisPaper[];
}

const HERO_BG = 'radial-gradient(1100px 500px at 78% 30%, #24398f 0%, transparent 60%), linear-gradient(135deg, #0c1640 0%, #0f1b4d 45%, #152560 100%)';

export const LandingPage: React.FC<LandingPageProps> = ({
  onOpenAuthModal,
  setActiveTab,
  onOpenPricingModal,
  onSelectExam,
  userProfile,
  papers = []
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);

  const isLoggedIn = Boolean(userProfile?.uid && userProfile.uid !== 'guest_user_1');

  const familyCounts = useMemo(() => {
    const map: Record<string, number> = {};
    examCatalog.forEach((e) => { map[e.id] = papersForFamily(e, papers).length; });
    return map;
  }, [papers]);
  const totalPapers = useMemo(() => {
    const ids = new Set<string>();
    examCatalog.forEach((e) => papersForFamily(e, papers).forEach((p) => ids.add(p.id)));
    return ids.size;
  }, [papers]);
  const liveFamilies = examCatalog.filter((e) => (familyCounts[e.id] || 0) > 0);

  // Search-as-you-type across the catalog (unchanged behaviour).
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return examCatalog.filter(e =>
      e.shortLabel.toLowerCase().includes(q) ||
      e.examLabel.toLowerCase().includes(q) ||
      e.state.toLowerCase().includes(q) ||
      e.subtests.toLowerCase().includes(q) ||
      e.examCodes.some(code => code.toLowerCase().includes(q)) ||
      papersForFamily(e, papers).some(p => `${p.code} ${p.title}`.toLowerCase().includes(q))
    );
  }, [searchQuery, papers]);

  const handleCardClick = (entry: ExamCatalogEntry) => {
    // Open to everyone (2026-10-08): guests can browse an exam's papers and
    // try 5 free questions before being asked to sign up.
    if (onSelectExam) onSelectExam(entry);
    setActiveTab('papers');
  };

  const handleExplore = () => {
    if (searchResults.length > 0) {
      handleCardClick(searchResults[0]);
      setSearchQuery('');
      setIsSearchFocused(false);
      return;
    }
    setActiveTab('papers');
  };

  const handleGetStarted = () => {
    if (!isLoggedIn) {
      onOpenAuthModal('signup', { actionType: 'general', contextTitle: 'Get Started' });
    } else {
      setActiveTab('dashboard');
    }
  };

  const examCountLabel = totalPapers > 0 ? `${totalPapers}+ exams` : 'Many exams';

  return (
    <div className="pb-10 max-w-7xl mx-auto -mt-6">
      {/* ================= HERO (full-bleed navy) ================= */}
      <section
        className="relative left-1/2 right-1/2 -mx-[50vw] w-screen overflow-hidden text-white"
        style={{ background: HERO_BG }}
      >
        <div className="absolute left-0 right-0 bottom-0 h-1 bg-gradient-to-r from-[#0E9594] via-cyan-400 to-indigo-500" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 flex flex-col lg:flex-row gap-10 items-center">
          {/* Left */}
          <div className="w-full lg:w-[58%] space-y-6">
            <span className="inline-flex items-center gap-2 text-xs font-bold text-cyan-200 border border-cyan-200/30 bg-teal-500/15 rounded-full px-3.5 py-1.5">
              <GraduationCap className="w-4 h-4" /> Your success in teaching starts here
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-[52px] font-extrabold leading-[1.08] tracking-tight">
              Prepare. Practice. Pass.
              <br />
              Your{' '}
              <span className="bg-gradient-to-r from-teal-300 via-sky-400 to-indigo-300 bg-clip-text text-transparent">
                Teacher Certification
              </span>{' '}
              Exams
            </h1>
            <p className="text-base sm:text-[17px] text-indigo-100/85 leading-relaxed max-w-xl">
              Practice questions, full timed mock exams and study guides for Praxis, CBEST, CSET and more. Built on official exam blueprints, so you walk in knowing what to expect.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
              {[
                { icon: ClipboardCheck, label: 'Real Exam Simulation', tint: 'bg-indigo-500/25' },
                { icon: Lightbulb, label: 'Detailed Explanations', tint: 'bg-pink-500/20' },
                { icon: TrendingUp, label: 'Progress Tracking', tint: 'bg-teal-500/30' },
                { icon: CalendarCheck, label: '1-on-1 Tutoring', tint: 'bg-amber-400/20' }
              ].map(({ icon: Icon, label, tint }) => (
                <div key={label} className="flex items-center gap-2.5 text-[13px] font-semibold text-indigo-50 leading-tight">
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tint}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </span>
                  {label}
                </div>
              ))}
            </div>

            {/* Search (search-as-you-type, unchanged behaviour) */}
            <div className="relative max-w-2xl">
              <form
                onSubmit={(e) => { e.preventDefault(); handleExplore(); }}
                className="flex items-center bg-white rounded-2xl p-1.5 pl-4 shadow-2xl"
              >
                <Search className="w-5 h-5 text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 150)}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search your state, subject or exam code (e.g. 5733)..."
                  className="flex-1 min-w-0 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 bg-transparent focus:outline-none"
                />
                <button
                  type="submit"
                  className="shrink-0 bg-[#0E9594] hover:bg-[#0b7f7e] text-white font-extrabold text-sm rounded-xl px-4 sm:px-5 py-3 flex items-center gap-1.5 transition cursor-pointer"
                >
                  Explore Exams <ArrowRight className="w-4 h-4" />
                </button>
              </form>
              {isSearchFocused && searchQuery.trim().length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden z-30 max-h-72 overflow-y-auto">
                  {searchResults.length > 0 ? (
                    searchResults.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          handleCardClick(item);
                          setIsSearchFocused(false);
                          setSearchQuery('');
                        }}
                        className="w-full p-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center justify-between gap-3 transition cursor-pointer"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-sm text-[#0f1b4d]">{item.shortLabel}</span>
                            <span className="text-[11px] px-2 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-600 font-semibold">{item.state}</span>
                            {(familyCounts[item.id] || 0) === 0 && (
                              <span className="text-[10px] font-bold text-slate-400">Coming soon</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.examLabel}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-xs text-slate-400">
                      No matching exams found. Browse all {examCatalog.length} exam families below.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[12.5px] text-indigo-200/80">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-300" /> 5 free questions per paper</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-300" /> No credit card needed</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-300" /> Built on official exam blueprints</span>
            </div>
          </div>

          {/* Right: illustration + true facts */}
          <div className="hidden lg:block relative w-[42%] h-[420px]">
            <div className="absolute left-0 top-4 w-[265px] h-[190px] rounded-2xl border-[9px] border-[#8b5e3c] shadow-2xl" style={{ background: 'linear-gradient(160deg,#134e4a,#0f3d3a)' }}>
              <p className="absolute left-5 top-7 text-[21px] leading-snug text-emerald-50 -rotate-3" style={{ fontFamily: '"Segoe Print","Comic Sans MS",cursive' }}>
                Better Teachers<br />Brighter Futures ♡
              </p>
            </div>
            <div className="absolute left-0 top-[190px] space-y-1.5">
              <div className="h-10 w-[220px] rounded-lg bg-[#0E9594] text-white font-black text-lg flex items-center pl-5 shadow-lg">Practice</div>
              <div className="h-10 w-[205px] ml-3 rounded-lg bg-indigo-500 text-white font-black text-lg flex items-center pl-5 shadow-lg">Learn</div>
              <div className="h-10 w-[230px] rounded-lg bg-amber-500 text-white font-black text-lg flex items-center pl-5 shadow-lg">Succeed</div>
            </div>
            <div className="absolute right-0 top-0 w-[215px] rounded-2xl border border-white/20 bg-white/10 backdrop-blur p-4 space-y-1">
              {[
                { icon: Layers, title: examCountLabel, sub: 'Praxis, CBEST, CSET, ParaPathways', tint: 'bg-teal-300/25' },
                { icon: Timer, title: '2 mocks per exam', sub: 'Timed, with score reports', tint: 'bg-indigo-300/25' },
                { icon: Gift, title: 'Try it free', sub: '5 questions, no sign up needed', tint: 'bg-amber-300/25' }
              ].map(({ icon: Icon, title, sub, tint }, i) => (
                <div key={title} className={`flex gap-3 items-center py-2.5 ${i < 2 ? 'border-b border-white/10' : ''}`}>
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tint}`}><Icon className="w-4 h-4 text-white" /></span>
                  <div>
                    <div className="text-lg font-extrabold leading-tight">{title}</div>
                    <div className="text-[11.5px] text-indigo-200/80">{sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="absolute right-2 top-[300px] text-teal-300 text-2xl -rotate-6 leading-tight" style={{ fontFamily: '"Segoe Script","Comic Sans MS",cursive' }}>
              Your Goals,<br />Our Support
            </p>
          </div>
        </div>
      </section>

      {/* Live platform ticker (renders nothing until feedEvents has posts) */}
      <LiveActivityFeed />

      {/* ================= Browse by exam family ================= */}
      <section className="pt-10">
        <div className="flex flex-col sm:flex-row sm:items-end gap-2 mb-5">
          <div>
            <h2 className="text-2xl font-extrabold text-[#0f1b4d]">Browse by exam family</h2>
            <p className="text-sm text-slate-500 mt-1">Pick your state or the national Praxis series to see every paper we cover.</p>
          </div>
          <button
            type="button"
            onClick={() => setActiveTab('papers')}
            className="sm:ml-auto text-sm font-extrabold text-[#0E9594] hover:text-[#0b7f7e] flex items-center gap-1 cursor-pointer"
          >
            View all exam families <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3.5">
          {examCatalog.map((entry) => (
            <ExamFamilyCard
              key={entry.id}
              entry={entry}
              paperCount={familyCounts[entry.id] || 0}
              tagLabel={entry.id === 'most-other-states' ? 'Most popular' : undefined}
              onOpen={handleCardClick}
            />
          ))}
          <button
            type="button"
            onClick={() => setActiveTab('papers')}
            className="rounded-2xl p-5 text-center text-white flex flex-col items-center justify-center bg-gradient-to-br from-[#0f1b4d] to-[#1b2f7a] hover:shadow-lg transition cursor-pointer min-h-[200px]"
          >
            <span className="text-3xl font-black text-teal-300">{examCatalog.length}</span>
            <span className="font-extrabold mt-1">Exam families</span>
            <span className="text-xs text-indigo-200/80 mt-1">
              {totalPapers > 0 ? `${totalPapers} papers` : 'Papers'} with practice, mocks and guides
              {liveFamilies.length < examCatalog.length ? `, more states coming soon` : ''}
            </span>
            <span className="mt-3 text-sm font-extrabold text-teal-300 flex items-center gap-1">Explore all <ArrowRight className="w-4 h-4" /></span>
          </button>
        </div>
      </section>

      {/* ================= CTA band ================= */}
      <section className="mt-10 rounded-3xl px-6 sm:px-8 py-6 text-white flex flex-col lg:flex-row lg:items-center gap-5 bg-gradient-to-r from-[#0f1b4d] to-[#1b2f7a]">
        <div>
          <h3 className="text-xl font-extrabold">Ready to start your journey?</h3>
          <p className="text-sm text-indigo-200/80 mt-1">Create a free account, pick your exam, and get a study plan built around your test date.</p>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-indigo-100">
          <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-teal-300" /> No credit card</span>
          <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-teal-300" /> 5 free questions per paper</span>
          <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-teal-300" /> Cancel anytime</span>
        </div>
        <div className="lg:ml-auto flex flex-wrap lg:flex-nowrap gap-2.5 shrink-0">
          <button
            type="button"
            onClick={handleGetStarted}
            className="bg-pink-500 hover:bg-pink-600 text-white font-extrabold text-sm rounded-xl px-5 py-3 flex items-center gap-1.5 whitespace-nowrap transition cursor-pointer"
          >
            {isLoggedIn ? 'Go to my dashboard' : 'Get Started Free'} <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onOpenPricingModal}
            className="border border-white/40 hover:bg-white/10 text-white font-bold text-sm rounded-xl px-4 py-3 whitespace-nowrap transition cursor-pointer"
          >
            All-Access, $35/mo
          </button>
        </div>
      </section>

      {/* ================= Trust strip ================= */}
      <section className="mt-6 bg-white border border-slate-200 rounded-3xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 p-5">
        {[
          { icon: ShieldCheck, title: 'Built on Official Blueprints', text: "Domains and weights match each exam's published outline." },
          { icon: Sparkles, title: 'AI-Powered Feedback', text: 'Instant scoring on essays and written responses.' },
          { icon: Users, title: 'Real People to Help', text: 'Free consultation and 1-on-1 tutoring.' },
          { icon: Star, title: 'Your Success Matters', text: 'Study plans and reminders built around your test date.' }
        ].map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex gap-3 items-start">
            <span className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#0E9594]" /></span>
            <div>
              <div className="text-sm font-extrabold text-[#0f1b4d]">{title}</div>
              <div className="text-xs text-slate-500 leading-relaxed mt-0.5">{text}</div>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};
